/**
 * Daten-Export page (Phase 3, §6.5). Downloads a complete data-ownership export
 * as JSON (`GET /api/export/all.json`) or a ZIP bundle of de-DE CSVs +
 * embedded JSON (`GET /api/export/all.zip`), optionally filtered to a single tax
 * year. Both downloads stream through `api.downloadFile` (which reads the
 * server's `Content-Disposition` filename) and are disabled offline.
 *
 * This is a normal page — it moves real financial records, never an Artifact.
 */
import { useMemo, useState } from 'react';
import { Download, FileArchive, FileJson } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Select, type SelectOption } from '../components/ui/Select.tsx';
import { useOnlineStatus } from '../context/OnlineStatusContext.tsx';
import { useToast } from '../context/ToastContext.tsx';
import { api, ApiRequestError } from '../lib/api.ts';

const ALL = 'all';
const HISTORY_YEARS = 10;

type ExportFormat = 'json' | 'zip';

function yearOptions(): SelectOption[] {
  const current = new Date().getFullYear();
  const options: SelectOption[] = [{ value: ALL, label: 'Alle Daten' }];
  for (let year = current; year > current - HISTORY_YEARS; year -= 1) {
    options.push({ value: String(year), label: String(year) });
  }
  return options;
}

export default function ExportPage() {
  const { online, guardOnline } = useOnlineStatus();
  const { toast } = useToast();
  const [year, setYear] = useState<string>(ALL);
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const options = useMemo(() => yearOptions(), []);

  async function download(format: ExportFormat) {
    if (!guardOnline()) return;
    setBusy(format);
    try {
      const query = year === ALL ? '' : `?year=${year}`;
      await api.downloadFile(
        `/api/export/all.${format}${query}`,
        `finance-tracker-export.${format}`,
      );
    } catch (error) {
      toast(error instanceof ApiRequestError ? error.message : 'Export fehlgeschlagen.', {
        variant: 'error',
      });
    } finally {
      setBusy(null);
    }
  }

  const downloadsDisabled = !online || busy !== null;

  return (
    <div>
      <PageHeader title="Daten-Export" subtitle="Alle Haushaltsdaten herunterladen" />

      <div className="w-full space-y-8 px-4 py-6 sm:px-6">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Exportiere deine kompletten Finanzdaten — Konten, Kategorien, Umsätze mit Aufteilungen,
          Regeln, Budgets, Kontostände und erkannte Überweisungen. Der Export enthält keine
          Zugangsdaten oder Schlüssel. Die JSON-Datei ist die vollständige, maßgebliche Fassung; das
          ZIP-Bündel enthält zusätzlich Excel-taugliche CSV-Dateien.
        </p>

        <div className="space-y-4">
          <div className="max-w-xs space-y-1.5">
            <label
              htmlFor="export-year"
              className="block text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              Zeitraum
            </label>
            <Select id="export-year" value={year} onValueChange={setYear} options={options} />
            <p className="text-xs text-slate-400 dark:text-slate-500">
              „Alle Daten“ enthält auch gelöschte Umsätze. Ein Jahr liefert einen Steuer-Export der
              echten Buchungen.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button onClick={() => void download('json')} disabled={downloadsDisabled}>
              <FileJson className="h-4 w-4" />
              {busy === 'json' ? 'Exportiere …' : 'JSON herunterladen'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => void download('zip')}
              disabled={downloadsDisabled}
            >
              <FileArchive className="h-4 w-4" />
              {busy === 'zip' ? 'Exportiere …' : 'ZIP herunterladen'}
            </Button>
          </div>

          {!online ? (
            <p className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
              <Download className="h-3.5 w-3.5" aria-hidden />
              Export ist offline nicht möglich.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
